<!-- components/modals/MergeDocumentsModalContent.vue -->
<script setup lang="ts">
import GlobalFormMessage from '@/components/forms/GlobalFormMessage.vue'
import { FilePdfIcon, ImageIcon } from '@/icons/'
import type { MediaDocument } from '@/types/media'

interface MediaDocumentWithSelection extends MediaDocument {
  selected?: boolean
}

defineProps<{
  selectedDocuments: MediaDocumentWithSelection[]
  mergingPdf: boolean
  mergeStep: string
  canMerge: boolean
  unmergeableFileNames: string
  hasUnmergeableFiles: boolean
  mergeableSelectedDocs: MediaDocumentWithSelection[]
  MAX_FILE_SIZE_MB: number
}>()

const emit = defineEmits<{
  (e: 'merge'): void
}>()

const isMergeableFile = (doc: MediaDocumentWithSelection): boolean => {
  const type = doc.fileType?.toLowerCase() || ''
  const title = doc.title?.toLowerCase() || ''
  if (type.includes('pdf') || title.includes('.pdf') || title.endsWith('pdf')) return true
  if (['jpeg', 'jpg', 'png', 'gif', 'bmp', 'webp', 'tiff', 'image/'].some(t => type.includes(t))) return true
  return ['.jpg', '.jpeg', '.png', '.gif', '.bmp', '.webp', '.tiff']
    .some(ext => title.includes(ext) || title.endsWith(ext.replace('.', '')))
}

const submitForm = () => {
  emit('merge')
  return true
}

defineExpose({
  submitForm
})
</script>

<template>
  <div class="space-y-4">
    <div v-if="mergeStep" class="mb-3">
      <GlobalFormMessage
        :message="mergeStep"
        type="loading"
        :show-icon="true"
        :dismissible="false"
        :auto-dismiss="false"
      />
    </div>

    <div v-if="hasUnmergeableFiles" class="bg-orange-50 border border-orange-200 rounded-lg p-3">
      <div class="flex items-start gap-2">
        <svg class="h-4 w-4 text-orange-400 mt-0.5 flex-shrink-0" viewBox="0 0 20 20" fill="currentColor">
          <path fill-rule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clip-rule="evenodd" />
        </svg>
        <p class="text-sm text-orange-700">
          <span class="font-medium">Skipped (non-mergeable):</span> {{ unmergeableFileNames }}
        </p>
      </div>
    </div>

    <div class="bg-blue-50 border border-blue-200 rounded-lg p-4">
      <h3 class="text-sm font-medium text-blue-800 mb-2">Documents to Merge ({{ mergeableSelectedDocs.length }})</h3>
      <ul class="space-y-1 max-h-48 overflow-y-auto">
        <li v-for="doc in mergeableSelectedDocs" :key="doc.id" class="flex items-center gap-2 text-sm text-blue-700">
          <FilePdfIcon v-if="isMergeableFile(doc) && doc.fileType?.includes('pdf')" class="w-4 h-4 flex-shrink-0" />
          <ImageIcon v-else-if="isMergeableFile(doc)" class="w-4 h-4 flex-shrink-0" />
          <span class="truncate">{{ doc.title }}</span>
        </li>
      </ul>
    </div>

    <div class="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
      <p class="text-xs text-yellow-700">
        <span class="font-medium">Note:</span> Images will be added as separate pages.
        The merged PDF will be downloaded to your computer and will not be saved to the server.
        Maximum merged file size: {{ MAX_FILE_SIZE_MB }}MB.
      </p>
    </div>
  </div>
</template>
